/*
A Generic class to create an HTML View 
Required: context, identifier, template 
Usage: var view = new SimpleView({context:document, identifier:'zee', template:'<h1>Hi,  <#=name#>!</h1>', name:'zee'});
*/
import addClass from './hooks/addClass';
import hideElement from './hooks/hideElement';
import observe from './hooks/observe';
import removeClass from './hooks/removeClass';
import addStyleTextInIframe from './hooks/addStyleTextInIframe';

export default class SimpleView {
    constructor(options) {
        // mandatory options
        this.options = options;
        if (typeof this.options !== 'object') {
            throw 'missing options';
        }
        if (!this.options.context) {
            throw 'context is mandatory';
        }
        this.identifier = this.options.identifier;
        this.template = this.options.template;
        this.rawContent = this.options.rawContent;
        this.context = this.options.context;
        this.afterRender = this.options.afterRender;
        this.beforeRender = this.options.beforeRender;
        this.containerSelector = this.options.containerSelector;
        this.isTemplateAlreadyParsed = this.options.isTemplateAlreadyParsed;
        // optional options
        this.style = this.options.style;
        this.classNames = this.options.classes;
        this.wrap = this.options.wrap;
        this.renderOninit = this.options.renderOninit !== 'false';
        this.elements = this.options.elements ? this.options.elements : [];
        this.model = this.options.model;
        this.isRendered = false;
        if (this.renderOninit) {
            this.render(this.options.model ? this.options.model : this.options);
        }
    }
    /* @param {any} model
    summary: renders the view, takes decisions and completes multiple tasks. */

    render(model) {
        if (this.beforeRender) {
            this.beforeRender();
        }
        const container = document.createElement('div');

        const target = this.options.targetElementSelector
            ? this.context.querySelector(this.options.targetElementSelector)
            : this.context.body;
        const wrapper =
            model && model.containerElemType
                ? this.context.createElement(model.containerElemType)
                : this.context.createElement('div');
        // if model contains a template use it with model = model
        // ELSE IF options contain a template then use it with model = options.model
        // ELSE IF options.rawContent given, write it out
        // ELSE the simple view will have no inner HTML
        if (this.isTemplateAlreadyParsed) {
            wrapper.innerHTML = this.template;
        } else {
            const getTemplate = () => {
                if (typeof this.template === 'function') {
                    return this.template(this.model);
                }
                return this.template;
            };
            wrapper.innerHTML =
                model && model.template
                    ? model.template(model)
                    : this.template && this.model
                        ? getTemplate()
                        : this.options.rawContent
                            ? this.options.rawContent
                            : '';
        }
        const containerId = model && model.containerId ? model.containerId : this.identifier;
        wrapper.setAttribute('id', containerId);
        this.elements.containerSelector = `#${containerId}`;
        if (this.wrap && !this.isTemplateAlreadyParsed) {
            container.setAttribute('id', containerId);
        }
        if (this.wrap) {
            // Add the View and preserved the old selection of question type
            container.innerHTML = wrapper.innerHTML;
            target.appendChild(container);
        } else {
            target.innerHTML = wrapper.innerHTML;
        }
        if (this.style) {
            this.addStyle(this.style);
        }
        if (this.classNames) {
            if (this.classNames.length > 0) {
                for (let i = 0; i < this.classNames.length; i++) {
                    this.addClass(this.classNames[i]);
                }
            }
        }

        if (this.afterRender) {
            this.afterRender();
        }
        // we use isViewRendered function to check if the view has rendered? in which case we can just show/hide subsequently
        this.isRendered = true;
    }

    isViewRendered() {
        return this.isRendered;
    }

    getContainerSelector() {
        return this.elements.containerSelector;
    }

    getContainer() {
        let wrapper = null;
        if (this.wrap) {
            wrapper = this.context.querySelector(this.getContainerSelector());
            return wrapper;
        }
        wrapper = this.context.querySelector(this.options.targetElementSelector);
        return wrapper;
    }

    unrender(remove) {
        const wrapperContainer = this.context.getElementById(this.identifier);
        if (wrapperContainer) {
            hideElement(wrapperContainer);
        }
        // remove altogether if asked
        if (remove == true) {
            try {
                this.context.body.removeChild(wrapperContainer);
            } catch (e) { }
        }
    }

    addStyle(css) {
        const cssArr = Array.isArray(css) ? css : [css]; // css could be a single obj or list of css obj's
        for (let i = 0; i < cssArr.length; i++) {
            addStyleTextInIframe(cssArr[i], undefined, undefined, this.context);
        }
    }

    // apply a css class on the wrapper div or actual container of the View
    addClass(className) {
        // var wrapper = this.getContainer();
        const templateContainer = this.context.querySelector(`#${this.identifier}`);
        if (templateContainer) {
            addClass(templateContainer, className);
        }
    }

    removeClass(className) {
        const templateContainer = this.context.querySelector(`#${this.identifier}`);
        if (templateContainer) {
            removeClass(templateContainer, className);
        }
    }

    // removes all classes and sets only this class if supplied
    resetClass(className) {
        const wrapper = this.getContainer();
        wrapper.className = ''; // clear previous classes
        if (className) {
            this.addClass(className);
        }
    }

    parse(simpleView) {
        this.options = simpleView.options;
        this.identifier = simpleView.identifier;
        this.template = simpleView.template;
        this.rawContent = simpleView.rawContent;
        this.context = simpleView.context;
        this.style = simpleView.style;
        this.wrap = simpleView.wrap;
        this.renderOninit = simpleView.renderOninit;
        this.elements = simpleView.elements;
        this.model = simpleView.model;
    }

    bind(ev, cb) {
        // not tested/used much yet
        let elMatched;
        try {
            elMatched = this.context.querySelector(this.elements.containerSelector);
        } catch (e) {
            throw 'invalid selector';
        }
        if (elMatched) {
            observe(elMatched, ev, cb.bind2(this, cb)); // invoke cb with in 'this' context
        }
    }
}
